// src/components/data-sources/utils/formatters.tsx

/**
 * Formats a date string into a readable format (e.g., "Jan 1, 2024")
 * @param date - ISO date string
 * @returns Formatted date string
 */
export const formatDate = (date: string): string => {
    const d = new Date(date);
    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

/**
 * Formats a file size in bytes to a human-readable string (e.g., "1.5 MB")
 * @param bytes - Size in bytes
 * @returns Formatted size string with appropriate unit
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Converts a mime type to a readable format (e.g., "application/pdf" to "PDF")
 * @param mimeType - MIME type string
 * @returns Human-readable file type
 */
export const formatMimeType = (mimeType: string): string => {
    if (!mimeType) return 'Unknown';

    const mimeMap: { [key: string]: string } = {
        'application/pdf': 'PDF',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
        'application/vnd.ms-excel': 'XLS',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
        'text/csv': 'CSV',
        'text/plain': 'TXT',
        'application/json': 'JSON',
        'application/vnd.ms-powerpoint': 'PPT',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
        'text/markdown': 'MD',
        'image/jpeg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF'
    };

    return mimeMap[mimeType] || mimeType.split('/').pop()?.toUpperCase() || 'Unknown';
};

/**
 * Formats a timestamp to show relative time (e.g., "2 hours ago")
 * @param date - ISO date string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (date: string | Date): string => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'week', seconds: 604800 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
        { label: 'second', seconds: 1 }
    ];

    for (const interval of intervals) {
        const value = Math.floor(diffInSeconds / interval.seconds);
        if (value > 0) {
            return `${value} ${interval.label}${value === 1 ? '' : 's'} ago`;
        }
    }

    return 'just now';
};

/**
 * Formats a filename to ensure it's valid and safe
 * @param filename - Original filename
 * @returns Sanitized filename
 */
export const formatSafeFilename = (filename: string): string => {
    // Remove invalid characters
    let safe = filename.replace(/[<>:"/\\|?*\x00-\x1F]/g, '');

    // Replace spaces with underscores (optional)
    // safe = safe.replace(/\s+/g, '_');

    // Limit length (common filesystem limits are around 255 bytes)
    if (safe.length > 255) {
        const ext = safe.lastIndexOf('.');
        if (ext > -1) {
            const name = safe.slice(0, ext);
            const extension = safe.slice(ext);
            safe = name.slice(0, 255 - extension.length) + extension;
        } else {
            safe = safe.slice(0, 255);
        }
    }

    return safe;
};

/**
 * Formats a number as a percentage string
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
    return `${value.toFixed(decimals)}%`;
};